import { z } from "zod";
import {
	eventAttendanceLogSchema,
	eventCancellationPolicySchema,
	eventCategorySchema,
	eventOccasionSchema,
	eventOccurrenceSchema,
	eventRegistrationCouponSchema,
	eventRegistrationSchema,
} from "../schemas";
import {
	adminAttendanceLogQuerySchema,
	adminAttendanceUpdateBodySchema,
	adminBulkCreateOccurrenceBodySchema,
	adminCategoryListQuerySchema,
	adminCategoryPathParamsSchema,
	adminCreateCategoryBodySchema,
	adminCreateOccasionBodySchema,
	adminCreateOccurrenceBodySchema,
	adminExportRegistrationsQuerySchema,
	adminOccasionListQuerySchema,
	adminOccasionPathParamsSchema,
	adminOccurrenceListQuerySchema,
	adminOccurrencePathParamsSchema,
	adminPaginationSchema,
	adminRegistrationPathParamsSchema,
	adminRegistrationsQuerySchema,
	adminUpdateCategoryBodySchema,
	adminUpdateOccasionBodySchema,
	adminUpdateOccasionsOrderBodySchema,
	adminUpdateOccurrenceBodySchema,
	adminUpdateRegistrationStatusBodySchema,
} from "../schemas/adminEventApi.schemas";

const paginationSchema = z.object({
	page: z.number().int().min(1),
	pageSize: z.number().int().min(1),
	totalItems: z.number().int().min(0),
	totalPages: z.number().int().min(0),
});

const createPaginatedResponseSchema = <T extends z.ZodTypeAny>(item: T) =>
	z.object({
		data: z.array(item),
		pagination: paginationSchema,
	});

export const adminEventEndpointContract = {
	category: {
		create: {
			path: "/m/event/category",
			method: "POST" as const,
			requestSchema: adminCreateCategoryBodySchema,
			responseSchema: eventCategorySchema,
		},
		list: {
			path: "/m/event/category",
			method: "GET" as const,
			querySchema: adminCategoryListQuerySchema,
			responseSchema: createPaginatedResponseSchema(eventCategorySchema),
		},
		read: {
			path: "/m/event/category/:eventCategoryId",
			method: "GET" as const,
			pathSchema: adminCategoryPathParamsSchema,
			responseSchema: z.object({
				category: eventCategorySchema,
				defaultCancellationPolicy: eventCancellationPolicySchema.nullable(),
			}),
		},
		update: {
			path: "/m/event/category/:eventCategoryId",
			method: "PUT" as const,
			pathSchema: adminCategoryPathParamsSchema,
			requestSchema: adminUpdateCategoryBodySchema,
			responseSchema: eventCategorySchema,
		},
		delete: {
			path: "/m/event/category/:eventCategoryId",
			method: "DELETE" as const,
			pathSchema: adminCategoryPathParamsSchema,
			responseSchema: z.object({ success: z.boolean() }),
		},
	},
	occasion: {
		create: {
			path: "/m/event/occasion",
			method: "POST" as const,
			requestSchema: adminCreateOccasionBodySchema,
			responseSchema: eventOccasionSchema,
		},
		list: {
			path: "/m/event/occasion",
			method: "GET" as const,
			querySchema: adminOccasionListQuerySchema,
			responseSchema: createPaginatedResponseSchema(eventOccasionSchema),
		},
		read: {
			path: "/m/event/occasion/:eventOccasionId",
			method: "GET" as const,
			pathSchema: adminOccasionPathParamsSchema,
			responseSchema: z.object({
				occasion: eventOccasionSchema,
				occurrences: z.array(eventOccurrenceSchema),
				cancellationPolicies: z.array(eventCancellationPolicySchema).optional(),
			}),
		},
		update: {
			path: "/m/event/occasion/:eventOccasionId",
			method: "PUT" as const,
			pathSchema: adminOccasionPathParamsSchema,
			requestSchema: adminUpdateOccasionBodySchema,
			responseSchema: eventOccasionSchema,
		},
		publish: {
			path: "/m/event/occasion/:eventOccasionId/publish",
			method: "PATCH" as const,
			pathSchema: adminOccasionPathParamsSchema,
			requestSchema: z
				.object({
					isPublished: z.boolean(),
					publishedAt: z.coerce.date().optional(),
				})
				.strict(),
			responseSchema: eventOccasionSchema,
		},
		delete: {
			path: "/m/event/occasion/:eventOccasionId",
			method: "DELETE" as const,
			pathSchema: adminOccasionPathParamsSchema,
			responseSchema: z.object({ success: z.boolean() }),
		},
		batchOrder: {
			path: "/m/event/occasion/order",
			method: "POST" as const,
			requestSchema: adminUpdateOccasionsOrderBodySchema,
			responseSchema: z.object({ success: z.boolean() }),
		},
	},
	occurrence: {
		create: {
			path: "/m/event/occurrence",
			method: "POST" as const,
			requestSchema: adminCreateOccurrenceBodySchema,
			responseSchema: eventOccurrenceSchema,
		},
		bulkCreate: {
			path: "/m/event/occurrence/bulk",
			method: "POST" as const,
			requestSchema: adminBulkCreateOccurrenceBodySchema,
			responseSchema: z.object({
				created: z.number().int().min(0),
			}),
		},
		list: {
			path: "/m/event/occurrence",
			method: "GET" as const,
			querySchema: adminOccurrenceListQuerySchema,
			responseSchema: createPaginatedResponseSchema(eventOccurrenceSchema),
		},
		read: {
			path: "/m/event/occurrence/:eventOccurrenceId",
			method: "GET" as const,
			pathSchema: adminOccurrencePathParamsSchema,
			responseSchema: z.object({
				occurrence: eventOccurrenceSchema.extend({
					registrations: z.array(z.any()).optional(),
					cancels: z.array(z.any()).optional(),
				}),
				registrationSummary: z.object({
					totalRegistrations: z.number().int().min(0),
					byStatus: z.record(
						z.string(),
						z.object({
							count: z.number().int().min(0),
							expectedTotal: z.number().int().min(0),
						}),
					),
				}),
			}),
		},
		update: {
			path: "/m/event/occurrence/:eventOccurrenceId",
			method: "PUT" as const,
			pathSchema: adminOccurrencePathParamsSchema,
			requestSchema: adminUpdateOccurrenceBodySchema,
			responseSchema: eventOccurrenceSchema,
		},
		display: {
			path: "/m/event/occurrence/:eventOccurrenceId/display",
			method: "PATCH" as const,
			pathSchema: adminOccurrencePathParamsSchema,
			requestSchema: z
				.object({
					isDisplayed: z.boolean(),
				})
				.strict(),
			responseSchema: eventOccurrenceSchema,
		},
		delete: {
			path: "/m/event/occurrence/:eventOccurrenceId",
			method: "DELETE" as const,
			pathSchema: adminOccurrencePathParamsSchema,
			responseSchema: z.object({ success: z.boolean() }),
		},
	},
	registrations: {
		list: {
			path: "/m/event/registrations",
			method: "GET" as const,
			querySchema: adminRegistrationsQuerySchema,
			responseSchema: createPaginatedResponseSchema(eventRegistrationSchema),
		},
		updateStatus: {
			path: "/m/event/registration/:eventRegistrationId/status",
			method: "PUT" as const,
			pathSchema: adminRegistrationPathParamsSchema,
			requestSchema: adminUpdateRegistrationStatusBodySchema,
			responseSchema: eventRegistrationSchema,
		},
		recordAttendance: {
			path: "/m/event/registration/:eventRegistrationId/attendance",
			method: "POST" as const,
			pathSchema: adminRegistrationPathParamsSchema,
			requestSchema: adminAttendanceUpdateBodySchema,
			responseSchema: eventRegistrationSchema,
		},
		export: {
			path: "/m/event/registration/export",
			method: "GET" as const,
			querySchema: adminExportRegistrationsQuerySchema,
			responseSchema: z.object({
				filename: z.string(),
				contentType: z.string(),
				data: z.string(),
			}),
		},
	},
	attendance: {
		getOccurrenceSummary: {
			path: "/m/event/attendance/occurrence/:eventOccurrenceId",
			method: "GET" as const,
			pathSchema: adminOccurrencePathParamsSchema,
			responseSchema: z.object({
				eventOccurrenceId: z.number().int().positive(),
				expectedCount: z.number().int().min(0),
				confirmedCount: z.number().int().min(0),
				attendedCount: z.number().int().min(0),
				noShowRate: z.number().min(0),
			}),
		},
		listLogs: {
			path: "/m/event/attendance/logs",
			method: "GET" as const,
			querySchema: adminAttendanceLogQuerySchema,
			responseSchema: createPaginatedResponseSchema(eventAttendanceLogSchema),
		},
		export: {
			path: "/m/event/attendance/export",
			method: "GET" as const,
			querySchema: adminAttendanceLogQuerySchema,
			responseSchema: z.object({
				filename: z.string(),
				contentType: z.string(),
				data: z.string(),
			}),
		},
	},
	coupons: {
		issue: {
			path: "/m/event/coupons/issue",
			method: "POST" as const,
			requestSchema: z
				.object({
					memberId: z.number().int().positive().optional(),
					eventRegistrationId: z.number().int().positive().optional(),
					templateId: z.number().int().positive(),
					reason: z.string().min(1).max(128),
				})
				.refine(
					(data) => Boolean(data.memberId || data.eventRegistrationId),
					"Issue request requires memberId or eventRegistrationId",
				),
			responseSchema: eventRegistrationCouponSchema,
		},
		activity: {
			path: "/m/event/coupons/activity",
			method: "GET" as const,
			querySchema: adminPaginationSchema,
			responseSchema: createPaginatedResponseSchema(
				eventRegistrationCouponSchema,
			),
		},
	},
	settings: {
		read: {
			path: "/m/event/settings",
			method: "GET" as const,
			responseSchema: z.object({
				bookLimitEnabled: z.boolean().nullable(),
				bookLimitDay: z.number().nullable(),
				bookLimitHour: z.number().nullable(),
				bookLimitMinute: z.number().nullable(),
				cancelLimitEnabled: z.boolean().nullable(),
				cancelLimitDay: z.number().nullable(),
				cancelLimitHour: z.number().nullable(),
				cancelLimitMinute: z.number().nullable(),
				bookRegistrationMessage: z.string().nullable(),
				bookCancelMessage: z.string().nullable(),
			}),
		},
		update: {
			path: "/m/event/settings",
			method: "PUT" as const,
			requestSchema: z.object({
				bookLimitEnabled: z.boolean().optional(),
				bookLimitDay: z.number().optional(),
				bookLimitHour: z.number().optional(),
				bookLimitMinute: z.number().optional(),
				cancelLimitEnabled: z.boolean().optional(),
				cancelLimitDay: z.number().optional(),
				cancelLimitHour: z.number().optional(),
				cancelLimitMinute: z.number().optional(),
				bookRegistrationMessage: z.string().optional(),
				bookCancelMessage: z.string().optional(),
			}),
			responseSchema: z.object({
				bookLimitEnabled: z.boolean().nullable(),
				bookLimitDay: z.number().nullable(),
				bookLimitHour: z.number().nullable(),
				bookLimitMinute: z.number().nullable(),
				cancelLimitEnabled: z.boolean().nullable(),
				cancelLimitDay: z.number().nullable(),
				cancelLimitHour: z.number().nullable(),
				cancelLimitMinute: z.number().nullable(),
				bookRegistrationMessage: z.string().nullable(),
				bookCancelMessage: z.string().nullable(),
			}),
		},
	},
} as const;

export type AdminEventEndpointContract = typeof adminEventEndpointContract;
